import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Checkbox,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import {
  addWeeks,
  eachDayOfInterval,
  format,
  isBefore,
  addDays,
} from "date-fns";

const WEEKDAYS = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];

interface Props {
  slotDates: string[];
  setSlotDates: (value: string[]) => void;
  startDate: string | null;
  setStartDate: (date: string | null) => void;
  endDate: string | null;
  setEndDate: (date: string | null) => void;
  repeatEveryWeeks: number;
  setRepeatEveryWeeks: (value: number) => void;
  continueForWeeks: number;
  setContinueForWeeks: (value: number) => void;
}

export default function CustomSelector({
  slotDates,
  setSlotDates,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  repeatEveryWeeks,
  setRepeatEveryWeeks,
  continueForWeeks,
  setContinueForWeeks,
}: Props) {
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [durationType, setDurationType] = useState<"weeks" | "date">("weeks");
  const [endDateLocal, setEndDateLocal] = useState<Date | null>(null);
  const [isMultiDayMode, setIsMultiDayMode] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Set initial startDate
  useEffect(() => {
    if (!startDate) {
      const today = new Date();
      setStartDate(today.toISOString());
    }
  }, [startDate, setStartDate]);

  useEffect(() => {
    if (slotDates.length && selectedDays.length === 0) {
      const uniqueDays = Array.from(
        new Set(slotDates.map((iso) => new Date(iso).getDay()))
      );
      setSelectedDays(uniqueDays);
      setIsMultiDayMode(uniqueDays.length > 1);
    }
  }, [slotDates, selectedDays]);

  // Validate and set endDate based on durationType
  useEffect(() => {
    if (!startDate) return;
    const start = new Date(startDate);

    if (durationType === "weeks") {
      if (continueForWeeks < 1) {
        setError("Duration must be at least 1 week");
        return;
      }
      const calculatedEnd = addWeeks(start, continueForWeeks);
      setEndDate(calculatedEnd.toISOString());
      setError(null);
    } else if (durationType === "date") {
      if (endDateLocal) {
        if (isBefore(endDateLocal, start)) {
          setError("End date must be after start date");
          return;
        }
        setEndDate(endDateLocal.toISOString());
        setError(null);
      }
    }
  }, [durationType, continueForWeeks, endDateLocal, startDate, setEndDate]);

  // Auto-calculate slotDates based on selected days with validation
  useEffect(() => {
    if (!startDate || !endDate || selectedDays.length === 0) {
      setSlotDates([]);
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    // Ensure end date is after start date
    if (isBefore(end, start)) {
      setError("End date must be after start date");
      setSlotDates([]);
      return;
    }

    const allDates = eachDayOfInterval({ start, end });
    const filtered = allDates.filter((date) => {
      const weekOffset = Math.floor(
        (date.getTime() - start.getTime()) / (7 * 24 * 60 * 60 * 1000)
      );
      return (
        weekOffset % repeatEveryWeeks === 0 &&
        selectedDays.includes(date.getDay())
      );
    });

    if (filtered.length === 0) {
      setError("No dates match the selected criteria");
    } else {
      setError(null);
    }

    setSlotDates(filtered.map((d) => d.toISOString()));
  }, [startDate, endDate, selectedDays, repeatEveryWeeks]);

  const handleDayToggle = (dayIndex: number) => {
    let updated: number[];

    if (isMultiDayMode) {
      updated = selectedDays.includes(dayIndex)
        ? selectedDays.filter((d) => d !== dayIndex)
        : [...selectedDays, dayIndex];
    } else {
      updated = [dayIndex];
    }

    setSelectedDays(updated);
  };

  // Disable dates before start date in the date picker
  const shouldDisableDate = (date: Date) => {
    if (!startDate) return false;
    return isBefore(date, new Date(startDate));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography variant="h6" gutterBottom>
          Class Scheduling & Recurrence
        </Typography>

        {/* Multi-day toggle */}
        <Box mb={2}>
          <Checkbox
            checked={isMultiDayMode}
            onChange={(e) => {
              setIsMultiDayMode(e.target.checked);
              if (!e.target.checked && selectedDays.length > 1) {
                setSelectedDays([selectedDays[0]]);
              }
            }}
          />
          <Typography variant="body2" component="span">
            Multi-day selection mode
          </Typography>
        </Box>

        {/* Weekday buttons */}
        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom>
            Days of the Week
          </Typography>
          <Grid container spacing={1}>
            {WEEKDAYS.map((day, index) => (
              <Grid item key={day}>
                <Button
                  variant={
                    selectedDays.includes(index) ? "contained" : "outlined"
                  }
                  onClick={() => handleDayToggle(index)}
                  sx={{ minWidth: 40 }}
                >
                  {day}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Repeat every */}
        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom>
            Recurrence Interval
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography>Repeat every</Typography>
            <TextField
              type="number"
              value={repeatEveryWeeks}
              onChange={(e) => {
                const value = Math.max(1, Number(e.target.value));
                setRepeatEveryWeeks(value);
              }}
              sx={{ width: 80 }}
              inputProps={{ min: 1 }}
            />
            <Typography>week(s)</Typography>
          </Box>
        </Box>

        {/* Duration */}
        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom>
            Duration
          </Typography>

          <Box display="flex" alignItems="center" mb={1}>
            <Checkbox
              checked={durationType === "weeks"}
              onChange={() => setDurationType("weeks")}
            />
            <Typography>Continue for</Typography>
            <TextField
              type="number"
              value={continueForWeeks}
              onChange={(e) => {
                const value = Math.max(1, Number(e.target.value));
                setContinueForWeeks(value);
              }}
              sx={{ width: 80, mx: 1 }}
              inputProps={{ min: 1 }}
              disabled={durationType !== "weeks"}
            />
            <Typography>weeks</Typography>
          </Box>

          <Box display="flex" alignItems="center">
            <Checkbox
              checked={durationType === "date"}
              onChange={() => setDurationType("date")}
            />
            <Typography>End on</Typography>
            <DatePicker
              value={endDateLocal}
              onChange={(newValue) => {
                if (newValue) {
                  setEndDateLocal(newValue);
                }
              }}
              sx={{ mx: 1, width: 180 }}
              disabled={durationType !== "date"}
              shouldDisableDate={shouldDisableDate}
              minDate={startDate ? addDays(new Date(startDate), 1) : undefined}
            />
          </Box>
        </Box>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
      </Box>
    </LocalizationProvider>
  );
}
